// ---------------------------------------------------------------------------
// Cage occupancy warnings
// ---------------------------------------------------------------------------

export interface CageWarning {
    destinationTempId: string;
    cageId: number;
    cageName: string;
    unlistedFishGroupId: number;
    unlistedFishGroupName: string;
}

export interface ComputeCageWarningsInput {
    sources: { fishGroupId: number }[];
    destinations: { tempId: string; cageId: number | null }[];
    locationCages: {
        cageId: number;
        cageName: string;
        activeFishGroup: { id: number; name: string } | null;
    }[];
}

/**
 * Surface destination cages that are occupied by a fish group not listed
 * among the wizard's sources. The backend would reject these with a
 * CageOccupiedException — we catch them earlier on the confirm step so the
 * operator can go back and either pick a different cage or add the occupant
 * as a source.
 *
 * The check is pure data: it does not consider the operation mode. Mode-
 * specific UIs (split, merge, etc.) already filter cage dropdowns to keep
 * unsafe picks unreachable in most cases; this is the belt-and-braces
 * verification that runs once the operator has reached the confirm step.
 */
export function computeCageWarnings(input: ComputeCageWarningsInput): CageWarning[] {
    const { sources, destinations, locationCages } = input;
    const sourceIds = new Set(sources.map((s) => s.fishGroupId));
    const cageById = new Map(locationCages.map((c) => [c.cageId, c]));

    const warnings: CageWarning[] = [];
    for (const dest of destinations) {
        if (dest.cageId == null) continue;
        const cage = cageById.get(dest.cageId);
        if (!cage) continue;
        const occupant = cage.activeFishGroup;
        if (occupant === null) continue;
        if (sourceIds.has(occupant.id)) continue;
        warnings.push({
            destinationTempId: dest.tempId,
            cageId: cage.cageId,
            cageName: cage.cageName,
            unlistedFishGroupId: occupant.id,
            unlistedFishGroupName: occupant.name,
        });
    }
    return warnings;
}
